// SearchResults.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import { getSocket } from "../utils/socket";
import Navbar from "../components/Navbar";
import ChatWidgetParticipants from "../components/ChatWidgetParticipants";
import ProfileStats from "../components/ProfileStats";
import UserSearch from "../components/UserSearch";
import UserList from "../components/UserList";
import SendGift from "./SendGift";

const SearchResults = () => {
  const { user: authUser, token, fetchWithRetry } = useAuth();
  const location = useLocation();
  const criteria = useMemo(() => location.state?.criteria || {}, [location.state]);
  const sortType = location.state?.sortType || "compatibility";

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [matches, setMatches] = useState([]);
  const [friends, setFriends] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoadingLike, setIsLoadingLike] = useState(false);
  const [isLoadingFriend, setIsLoadingFriend] = useState(false);
  const [likeStates, setLikeStates] = useState({});
  const [friendRequestStates, setFriendRequestStates] = useState({});
  const [tooltip, setTooltip] = useState(null);
  const [isTooltipFading, setIsTooltipFading] = useState(false);
  const [sortBy, setSortBy] = useState(sortType);
  const [showPhotoGallery, setShowPhotoGallery] = useState(null);
  const [chatUsers, setChatUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const usersPerPage = 18;

  // State cho quản lý coin và quà tặng
  const [coinBalance, setCoinBalance] = useState(0);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  const showTooltip = useCallback((message, type = "success") => {
    setTooltip({ message, type });
    setTimeout(() => {
      setIsTooltipFading(true);
      setTimeout(() => {
        setTooltip(null);
        setIsTooltipFading(false);
      }, 300);
    }, 1500);
  }, [setTooltip, setIsTooltipFading]);

  const fetchAllUsers = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        setError(null);
        const query = new URLSearchParams({
          page: page,
          limit: usersPerPage,
          sort: sortBy,
          gender: criteria.gender || "",
          minAge: criteria.ageRange?.minAge || "",
          maxAge: criteria.ageRange?.maxAge || "",
          country: criteria.locationText?.country || "",
          state: criteria.locationText?.district || "",
          city: criteria.locationText?.city || "",
          online: criteria.isOnline || false,
          "appearance.hairColor": criteria.appearance?.hairColor || "",
          "appearance.eyeColor": criteria.appearance?.eyeColor || "",
          "appearance.bodyType": criteria.appearance?.bodyType || "",
          "appearance.ethnicity": criteria.appearance?.ethnicity || "",
          "appearance.bodyArt": criteria.appearance?.bodyArt || "",
          "appearance.appearanceRating": criteria.appearance?.appearanceRating || "",
          "lifestyle.drinking": criteria.lifestyle?.drinking || "",
          "lifestyle.smoking": criteria.lifestyle?.smoking || "",
          "lifestyle.maritalStatus": criteria.lifestyle?.maritalStatus || "",
          "lifestyle.hasChildren": criteria.lifestyle?.hasChildren || "",
          "lifestyle.numberOfChildren": criteria.lifestyle?.numberOfChildren || "",
          "lifestyle.oldestChild": criteria.lifestyle?.oldestChild || "",
          "lifestyle.youngestChild": criteria.lifestyle?.youngestChild || "",
          "lifestyle.wantsChildren": criteria.lifestyle?.wantsChildren || "",
          "lifestyle.hasPets": criteria.lifestyle?.hasPets || "",
          "lifestyle.occupation": criteria.lifestyle?.occupation || "",
          "culture.nationality": criteria.culture?.nationality || "",
          "culture.education": criteria.culture?.education || "",
          "culture.primaryLanguage": criteria.culture?.primaryLanguage || "",
          "culture.englishAbility": criteria.culture?.englishAbility || "",
          "culture.vietnameseAbility": criteria.culture?.vietnameseAbility || "",
          "culture.religion": criteria.culture?.religion || "",
          zodiacSign: criteria.zodiacSign || "",
          lunarYear: criteria.lunarYear || "",
          userId: authUser?._id || "",
        }).toString();
        const res = await fetchWithRetry({
          method: "get",
          url: `/api/users/all-users?${query}`,
        });
        setUsers(res.data.users || []);
        setTotalPages(res.data.totalPages || 1);
        setTotalUsers(res.data.total || 0);
        setCurrentPage(res.data.currentPage || 1);
      } catch (error) {
        console.error("Lỗi khi lấy danh sách người dùng:", error.response || error);
        setError(
          error.response?.status === 401
            ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
            : error.response?.data?.message ||
              "Không thể tải danh sách người dùng. Vui lòng kiểm tra kết nối."
        );
        setUsers([]);
      } finally {
        setLoading(false);
      }
    },
    [sortBy, criteria, authUser, fetchWithRetry]
  );

  const fetchMatches = useCallback(async () => {
    try {
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/users/matches",
      });
      const fetchedMatches = res.data.matches || [];
      setMatches(fetchedMatches);
      const initialLikeStates = fetchedMatches.reduce((acc, match) => {
        const likedUserId = match.likedUserId._id
          ? String(match.likedUserId._id)
          : String(match.likedUserId);
        if (String(match.userId._id || match.userId) === String(authUser._id)) {
          acc[likedUserId] = true;
        }
        return acc;
      }, {});
      setLikeStates(initialLikeStates);
    } catch (error) {
      console.error("Lỗi fetchMatches:", error.response ? error.response.data : error.message);
      setError(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.data?.message || "Không thể tải danh sách ghép đôi."
      );
    }
  }, [authUser, fetchWithRetry]);

  const fetchFriends = useCallback(async () => {
    try {
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/users/friends",
      });
      const fetchedFriends = res.data.friends || [];
      setFriends(fetchedFriends);
    } catch (error) {
      console.error("Lỗi fetchFriends:", error.response ? error.response.data : error.message);
      setError(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.data?.message || "Không thể tải danh sách bạn bè."
      );
    }
  }, [fetchWithRetry]);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await fetchWithRetry({
        method: "get",
        url: "/api/users/notifications",
      });
      setNotifications(res.data);
      const unread = res.data.filter((n) => !n.read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error("Lỗi khi lấy thông báo:", error.response ? error.response.data : error.message);
      setError(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.data?.message || "Không thể tải thông báo."
      );
    }
  }, [fetchWithRetry]);

  useEffect(() => {
    if (authUser) {
      const fetchData = async () => {
        await fetchFriends();
        await fetchAllUsers(currentPage);
        await Promise.all([fetchMatches(), fetchNotifications()]);
      };

      fetchData();

      const pollInterval = setInterval(fetchNotifications, 10000);

      const socket = getSocket();
      if (!socket.connected) {
        socket.connect();
      }

      socket.on("connect", () => {
        console.log("Kết nối WebSocket thành công:", socket.id);
        socket.emit("joinRoom", authUser._id);
      });

      socket.on("notification", (notification) => {
        setNotifications((prev) => [notification, ...prev]);
        setUnreadCount((prev) => prev + 1);
      });

      socket.on("coinBalanceUpdate", (data) => {
        setCoinBalance(data.coinBalance);
        showTooltip(`Số dư coin của bạn đã được cập nhật: ${data.coinBalance} coin`, "success");
      });

      return () => {
        clearInterval(pollInterval);
        socket.off("notification");
        socket.off("coinBalanceUpdate");
      };
    }
  }, [
    authUser,
    token,
    fetchAllUsers,
    fetchMatches,
    fetchFriends,
    fetchNotifications,
    currentPage,
    showTooltip,
  ]);

  const handleLike = async (likedUserId) => {
    if (isLoadingLike) return;
    setIsLoadingLike(true);
    try {
      if (!authUser || !authUser._id) {
        throw new Error("Người dùng chưa được tải, vui lòng thử lại!");
      }
      const isAlreadyLiked = likeStates[likedUserId];
      if (isAlreadyLiked) {
        throw new Error("Bạn đã Like người dùng này!");
      }
      setLikeStates((prev) => ({ ...prev, [likedUserId]: true }));
      const response = await fetchWithRetry({
        method: "put",
        url: "/api/users/like",
        data: { likedUserId: String(likedUserId) },
      });
      showTooltip(response.data.message || "Thích thành công!", "success");
      await Promise.all([fetchMatches(), fetchNotifications()]);
    } catch (error) {
      console.error("Lỗi khi gửi Like:", error.response?.data || error.message);
      setLikeStates((prev) => ({ ...prev, [likedUserId]: false }));
      showTooltip(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.data?.message || error.message || "Lỗi khi gửi thích",
        "error"
      );
    } finally {
      setIsLoadingLike(false);
    }
  };

  const handleUnlike = async (likedUserId) => {
    if (isLoadingLike) return;
    setIsLoadingLike(true);
    try {
      if (!authUser || !authUser._id) {
        throw new Error("Người dùng chưa được tải, vui lòng thử lại!");
      }
      const isLiked = likeStates[likedUserId];
      if (!isLiked) {
        throw new Error("Bạn chưa Like người dùng này!");
      }
      setLikeStates((prev) => ({ ...prev, [likedUserId]: false }));
      setMatches((prev) =>
        prev.filter((m) => String(m.likedUserId._id || m.likedUserId) !== likedUserId)
      );
      const response = await fetchWithRetry({
        method: "delete",
        url: "/api/users/unlike",
        data: { likedUserId: String(likedUserId) },
      });
      showTooltip(response.data.message || "Bỏ thích thành công!", "success");
      await Promise.all([fetchMatches(), fetchNotifications()]);
    } catch (error) {
      console.error("Lỗi khi hủy Like:", error.response?.data || error.message);
      setLikeStates((prev) => ({ ...prev, [likedUserId]: true }));
      showTooltip(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.data?.message || error.message || "Lỗi khi bỏ thích",
        "error"
      );
    } finally {
      setIsLoadingLike(false);
    }
  };

  const sendFriendRequest = async (receiverId) => {
    if (isLoadingFriend) return;
    setIsLoadingFriend(true);
    try {
      if (!authUser || !authUser._id) {
        throw new Error("Người dùng chưa được tải, vui lòng thử lại!");
      }
      setFriendRequestStates((prev) => ({ ...prev, [receiverId]: true }));
      const response = await fetchWithRetry({
        method: "put",
        url: "/api/users/friend-request",
        data: { receiverId },
      });
      if (response.status === 200) {
        showTooltip(response.data.message || "Gửi yêu cầu kết bạn thành công!", "success");
      } else {
        throw new Error(response.data.message || "Lỗi khi gửi yêu cầu kết bạn");
      }
    } catch (error) {
      console.error("Lỗi khi gửi Friend Request:", error.response ? error.response.data : error);
      setFriendRequestStates((prev) => ({ ...prev, [receiverId]: false }));
      showTooltip(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.data?.message || error.message || "Lỗi khi gửi yêu cầu kết bạn",
        "error"
      );
    } finally {
      setIsLoadingFriend(false);
    }
  };

  const cancelFriendRequest = async (receiverId) => {
    if (isLoadingFriend) return;
    setIsLoadingFriend(true);
    try {
      if (!authUser || !authUser._id) {
        throw new Error("Người dùng chưa được tải, vui lòng thử lại!");
      }
      setFriendRequestStates((prev) => ({ ...prev, [receiverId]: false }));
      const response = await fetchWithRetry({
        method: "put",
        url: "/api/users/cancel-friend-request",
        data: { receiverId },
      });
      if (response.status === 200) {
        showTooltip(response.data.message || "Hủy yêu cầu kết bạn thành công!", "success");
      } else {
        throw new Error(response.data.message || "Lỗi khi hủy yêu cầu kết bạn");
      }
    } catch (error) {
      console.error("Lỗi khi hủy Friend Request:", error.response ? error.response.data : error);
      setFriendRequestStates((prev) => ({ ...prev, [receiverId]: true }));
      showTooltip(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.data?.message || error.message || "Lỗi khi hủy yêu cầu kết bạn",
        "error"
      );
    } finally {
      setIsLoadingFriend(false);
    }
  };

  const handleToggleNotifications = async (notificationId, action) => {
    if (!token) {
      showTooltip("Không có token, vui lòng đăng nhập lại!", "error");
      return;
    }

    if (!action || !notificationId) {
      return;
    }

    try {
      let response;
      if (action === "read") {
        response = await fetchWithRetry({
          method: "put",
          url: `/api/users/notifications/${notificationId}/read`,
          data: {},
        });
      } else if (action === "delete") {
        response = await fetchWithRetry({
          method: "delete",
          url: `/api/users/notifications/${notificationId}`,
        });
      } else {
        console.warn(`Hành động không hợp lệ: ${action}`);
        return;
      }

      if (response.status === 200) {
        if (action === "read") {
          setNotifications((prev) =>
            prev.map((n) => (n._id === notificationId ? { ...n, read: true } : n))
          );
          setUnreadCount((prev) => prev - 1);
        } else if (action === "delete") {
          setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
          setUnreadCount((prev) => {
            const deletedNotification = notifications.find((n) => n._id === notificationId);
            return deletedNotification && !deletedNotification.read ? prev - 1 : prev;
          });
        }
      }
    } catch (error) {
      console.error(`Lỗi khi ${action} thông báo:`, error.response ? error.response.data : error.message);
      showTooltip(
        error.response?.status === 401
          ? "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!"
          : error.response?.data?.message || `Lỗi khi ${action} thông báo!`,
        "error"
      );
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchAllUsers(page);
  };

  const handleSendGift = (userId) => {
    const selectedUser = users.find((user) => user._id === userId);
    if (selectedUser) {
      setSelectedUserId({
        membershipId: selectedUser.membershipId || null,
        id: selectedUser._id,
      });
      setShowGiftModal(true);
    } else {
      showTooltip("Không tìm thấy người dùng!", "error");
    }
  };

  const handleGiftSent = useCallback(
    async (gift) => {
      if (!selectedUserId?.id) {
        showTooltip("Không thể gửi quà: Người nhận không hợp lệ!", "error");
        return;
      }
      if (!gift || !gift._id) {
        showTooltip("Không thể gửi quà: Quà tặng không hợp lệ!", "error");
        return;
      }
  
      try {
        const response = await fetchWithRetry({
          method: "post",
          url: "/api/users/send-gift",
          data: { receiverId: selectedUserId.id, giftId: gift._id },
        });
  
        if (response.status === 200) {
          setShowGiftModal(false);
          showTooltip(`Đã gửi quà "${gift.name}" thành công!`, "success");
          await fetchNotifications();
        } else {
          throw new Error("Phản hồi từ server không hợp lệ!");
        }
      } catch (error) {
        console.error("Lỗi khi gửi quà tặng:", error);
        let errorMessage = "Không thể gửi quà tặng, vui lòng thử lại!";
        if (error.response) {
          if (error.response.status === 400) {
            errorMessage = error.response.data.message || "Dữ liệu gửi đi không hợp lệ!";
          } else if (error.response.status === 401) {
            errorMessage = "Phiên đăng nhập hết hạn. Vui lòng đăng nhập lại!";
          } else if (error.response.status === 500) {
            errorMessage = "Lỗi server, vui lòng thử lại sau!";
          } else {
            errorMessage = error.response.data.message || errorMessage;
          }
        }
        showTooltip(errorMessage, "error");
      }
    },
    [fetchWithRetry, selectedUserId, showTooltip, fetchNotifications]
  );

  const handleViewPhotos = (user) => {
    setShowPhotoGallery(user);
  };

  const handleOpenChat = (userId) => {
    if (!userId || !authUser || !authUser._id) {
      showTooltip("Không thể mở chat. Dữ liệu người dùng không hợp lệ!", "error");
      return;
    }
    const isValidUser = [...users, ...friends].some(
      (u) => u._id && String(u._id) === String(userId)
    );
    if (!isValidUser) {
      showTooltip("Người dùng không tồn tại trong danh sách!", "error");
      return;
    }
    setChatUsers((prev) => [...new Set([...prev, userId])]);
  };

  if (!authUser) {
    return <p className="text-center text-gray-600">Đang tải...</p>;
  }

  const filteredUsers = users.filter((user) => {
    if (!user || !user._id || !authUser || !authUser._id) return false;
    const isCurrentUser = String(user._id) === String(authUser._id);
    const isFriend = friends.some(
      (friend) => friend && friend._id && String(friend._id) === String(user._id)
    );
    return !isCurrentUser && !isFriend;
  });

  return (
    <div className="font-sans min-h-screen bg-gray-100">
      <Navbar
        unreadCount={unreadCount}
        unreadNotificationCount={unreadCount}
        notifications={notifications}
        onToggleNotifications={handleToggleNotifications}
      />

      <div className="pt-20 max-w-[1600px] mx-auto p-8">
        <ProfileStats authUser={authUser} coinBalance={coinBalance} />
        <UserSearch sortBy={sortBy} setSortBy={setSortBy} />
        {loading ? (
          <p className="text-center text-gray-500">Đang tải danh sách người dùng...</p>
        ) : error ? (
          <p className="text-red-500 text-center mb-4">{error}</p>
        ) : (
          <UserList
            filteredUsers={filteredUsers}
            authUser={authUser}
            likeStates={likeStates}
            friendRequestStates={friendRequestStates}
            matches={matches}
            friends={friends}
            isLoadingLike={isLoadingLike}
            isLoadingFriend={isLoadingFriend}
            handleLike={handleLike}
            handleUnlike={handleUnlike}
            sendFriendRequest={sendFriendRequest}
            cancelFriendRequest={cancelFriendRequest}
            handleOpenChat={handleOpenChat}
            handleSendGift={handleSendGift}
            handleViewPhotos={handleViewPhotos}
            currentPage={currentPage}
            totalPages={totalPages}
            totalUsers={totalUsers}
            handlePageChange={handlePageChange}
            showTooltip={showTooltip}
            loading={loading}
            skeletonCount={48}
          />
        )}
        {showGiftModal && selectedUserId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">Chọn Quà Tặng</h2>
                <button
                  onClick={() => setShowGiftModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  X
                </button>
              </div>
              <SendGift
                selectedUserId={selectedUserId}
                onClose={() => setShowGiftModal(false)}
                onGiftSent={handleGiftSent}
              />
            </div>
          </div>
        )}
        {showPhotoGallery && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  Thư viện ảnh của {showPhotoGallery.name}
                </h2>
                <button
                  onClick={() => setShowPhotoGallery(null)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  X
                </button>
              </div>
              {showPhotoGallery.photos && showPhotoGallery.photos.length > 0 ? (
                <div className="grid grid-cols-3 gap-4">
                  {showPhotoGallery.photos.map((photo, index) => (
                    <div key={index} className="relative">
                      <img
                        src={photo.photoUrl}
                        alt={`Gallery item ${index + 1} for ${showPhotoGallery.name}`}
                        className="w-full h-40 object-cover rounded-lg"
                        onError={(e) => (e.target.src = "https://placehold.co/150x150")}
                      />
                      {photo.caption && (
                        <p className="text-sm text-gray-600 mt-1">{photo.caption}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500">Không có ảnh nào để hiển thị.</p>
              )}
            </div>
          </div>
        )}
        {tooltip && (
          <div
            className={`fixed top-4 right-4 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center space-x-2 ${
              isTooltipFading ? "animate-fade-out" : "animate-slide-in"
            } ${tooltip.type === "success" ? "bg-green-500" : tooltip.type === "info" ? "bg-blue-500" : "bg-red-500"}`}
          >
            <span>{tooltip.message}</span>
          </div>
        )}
        {chatUsers.length > 0 && (
          <ChatWidgetParticipants
            onSelectChat={chatUsers[chatUsers.length - 1]}
            onClose={() => setChatUsers([])}
            users={users}
            friendsList={friends}
          />
        )}
      </div>
    </div>
  );
};

export default SearchResults;